import {
  Banner,
  Button,
  ErrorMessage,
  FieldLabel,
  ModalActions,
  ModalContent,
  TextInput,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { StripeCardElementChangeEvent } from '@stripe/stripe-js'
import React, { FC, useState, useEffect, useMemo } from 'react'
import { trackUserEvent } from '@vend/utilities'
import { useT } from '@transifex/react'
import {
  LOGGING_EXCLUDED_STRIPE_ERRORS,
  LSPAY_STEPS,
  STRIPE_CNP_VALIDATION_ERRORS,
  STRIPE_DECLINE_CODE,
  STRIPE_ERROR_CODE,
  SWIPER_ERROR_CODES,
} from '../../helpers/lspayConstants'
import { useTrackLSPAYError, TAG } from '../../hooks/errorTracking'
import { TimerStarterFunction } from '../../hooks/timeTracking'
import { confirmPayment, extractFromSecret } from '../../helpers/paymentIntent'
import { WEB_REGISTER } from '../../../../web-register.constant'
import { useCNPValidationErrorMessages } from '../../hooks/useCNPValidationErrorMessages'
import { useStylesReady } from '../../hooks/useStylesReady'
import { Loading } from './Loading'

// Error Messages for CNP Flow
const DEFAULT_PAYMENT_ERROR_MESSAGE =
  'Something went wrong and the payment has not been taken. Please try again.'
const COMPLETE_HIGHLIGHTED_FIELDS_ERROR_MESSAGE =
  'Please complete the highlighted fields to continue.'
const INSUFFICIENT_FUNDS_ERROR_MESSAGE =
  'The customer’s card has insufficient funds. You can try with a different card or cancel this payment and choose another payment type.'
export const DEFAULT_INLINE_VALIDATION_ERROR_MESSAGE =
  'Invalid or incomplete card details. Review all fields and try again.'

const isValidCardElementInput = (
  event: StripeCardElementChangeEvent
): boolean => event.complete && !event.error && !event.empty

// Create a function to generate Stripe styles dynamically
const createCardElementStyle = () => {
  // Force light theme colors for Stripe elements to ensure readability
  const backgroundColor = '#ffffff'
  const textColor = '#333333'
  const placeholderColor = '#666666'
  const errorColor = '#d32f2f'

  return {
    base: {
      fontFamily:
        'lato, "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '15px',
      backgroundColor,
      color: textColor,
      '::placeholder': {
        color: placeholderColor,
      },
      ':-webkit-autofill': {
        backgroundColor,
        color: textColor,
      },
    },
    invalid: {
      color: errorColor,
      iconColor: errorColor,
    },
  }
}
interface Props {
  close: () => void
  onSuccess: (intentId: string) => void
  setDisableTerminalPayment: (disableTerminalPayment: boolean) => void
  clientSecret: string
  trackTiming: TimerStarterFunction
  hasMoto: boolean
  registerId: string
}

export const CardCollectionForm: FC<Props> = ({
  close,
  onSuccess,
  clientSecret,
  setDisableTerminalPayment,
  trackTiming,
  hasMoto,
  registerId,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const getValidationErrorMessage = useCNPValidationErrorMessages()
  const t = useT()

  const [processing, setProcessing] = useState<boolean>(false)
  const [cardholderName, setCardHolderName] = useState<string>()
  const [isValid, setIsValid] = useState<boolean>(false)
  const [cardErrorMessage, setCardErrorMessage] = useState<string>()
  const [bannerErrorMessage, setBannerErrorMessage] = useState<string>()

  // Use custom hook for CSS detection
  const stylesReady = useStylesReady()

  const trackError = useTrackLSPAYError(TAG.cardNotPresentPayment)

  // Generate card element styles with light theme colors
  const cardElementStyle = useMemo(() => {
    return createCardElementStyle()
  }, [])

  // the terminal payment tab should be disabled whenever the form is processing
  useEffect(() => {
    setDisableTerminalPayment(processing)
  }, [setDisableTerminalPayment, processing])

  if (!stripe || !elements || !stylesReady) {
    return <Loading />
  }

  // confirm the payment with Stripe (without MOTO)
  const confirmWithStripe = async () => {
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      '3D Secure authentication required',
      {
        intent: extractFromSecret(clientSecret),
      }
    )
    setBannerErrorMessage(undefined)
    setProcessing(true)
    const stopProcessTimer = trackTiming(LSPAY_STEPS.CNP_PROCESS)
    const cardElement = elements.getElement(CardElement)

    // This shouldn't happen, but we handle the error regardless
    if (!cardElement) {
      const errorMessage = '[lspay] Failed to find cardElement'
      setProcessing(false)
      trackError(errorMessage, errorMessage)
      ToastNotifications.negative(
        t(DEFAULT_PAYMENT_ERROR_MESSAGE, {
          _context: 'Lspay CNP failed to find cardElement error message',
        })
      )
      return
    }

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: cardholderName,
        },
      },
    })

    stopProcessTimer()

    if (result.error) {
      const { error } = result

      setProcessing(false)
      trackUserEvent(
        WEB_REGISTER.DISPLAY_NAME,
        '3D Secure authentication failed',
        {
          intent: extractFromSecret(clientSecret),
        }
      )

      if (error.code === STRIPE_ERROR_CODE.INCOMPLETE_ZIP) {
        // The inline error is handled by `onCardElementChange`
        ToastNotifications.negative(
          t(COMPLETE_HIGHLIGHTED_FIELDS_ERROR_MESSAGE, {
            _context: 'Lspay CNP complete highlighted fields error message',
          })
        )
        return
      }

      const insufficient_funds: STRIPE_DECLINE_CODE = 'insufficient_funds'
      if (error.decline_code === insufficient_funds) {
        setBannerErrorMessage(
          t(INSUFFICIENT_FUNDS_ERROR_MESSAGE, {
            _context: 'Lspay CNP insufficient funds error message',
          })
        )
        return
      }

      setBannerErrorMessage(
        t(DEFAULT_PAYMENT_ERROR_MESSAGE, {
          _context:
            'Error message for Lspay CNP failed to confirm payment error message',
        })
      )
      if (
        error.code &&
        LOGGING_EXCLUDED_STRIPE_ERRORS.includes(error.code as STRIPE_ERROR_CODE)
      ) {
        // if the error code is in the list of excluded codes, we don't want to log it
        return
      }
      trackError(
        '[lspay] Failed to collect card data.',
        new Error(error.message),
        {
          stripeError: error,
        },
        [STRIPE_ERROR_CODE.INCORRECT_CVC, STRIPE_ERROR_CODE.EXPIRED]
      )
      return
    }

    onSuccess(result.paymentIntent.id)
  }

  const confirmWithSwiper = async () => {
    setBannerErrorMessage(undefined)
    setProcessing(true)
    const stopProcessTimer = trackTiming(LSPAY_STEPS.CNP_PROCESS)
    const cardElement = elements.getElement(CardElement)

    // This shouldn't happen, but we handle the error regardless
    if (!cardElement) {
      const errorMessage = '[lspay] Failed to find cardElement'
      setProcessing(false)
      trackError(errorMessage, errorMessage)
      ToastNotifications.negative(
        t(DEFAULT_PAYMENT_ERROR_MESSAGE, {
          _context: 'Lspay CNP failed to find cardElement error message',
        })
      )
      return
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: cardholderName,
      },
    })

    // todo: handle the error when failed to create payment method
    if (error) {
      stopProcessTimer()
      setProcessing(false)
      setBannerErrorMessage(
        t(DEFAULT_PAYMENT_ERROR_MESSAGE, {
          _context: 'Lspay CNP failed to create payment error message',
        })
      )
      return
    }

    if (!paymentMethod?.id) {
      const errorMessage = '[lspay] Missing paymentMethodId'
      setProcessing(false)
      trackError(errorMessage, errorMessage)
      ToastNotifications.negative(
        t(DEFAULT_PAYMENT_ERROR_MESSAGE, {
          _context: 'Lspay CNP missing paymentMethodId error message',
        })
      )
      return
    }

    const intentId = extractFromSecret(clientSecret)
    confirmPayment({
      registerId,
      intentId,
      paymentMethodId: paymentMethod.id,
    })
      .then(() => {
        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          '3D Secure authentication bypassed',
          {
            intent: extractFromSecret(clientSecret),
          }
        )
        stopProcessTimer()
        onSuccess(intentId)
      })
      .catch(error => {
        stopProcessTimer()
        setProcessing(false)

        // fallback to confirmWithStripe flow (without MOTO) when MOTO transactions are not exempted from SCA by the cardholder’s bank
        if (error?.code === SWIPER_ERROR_CODES.LSPAY_CARD_AUTH_REQUIRED) {
          // todo: we should show a confirm modal before starting confirmWithStripe
          confirmWithStripe()
          return
        }
        if (error?.code === SWIPER_ERROR_CODES.LSPAY_CARD_INSUFFICIENT_FUND) {
          setBannerErrorMessage(
            t(INSUFFICIENT_FUNDS_ERROR_MESSAGE, {
              _context: 'Lspay CNP insufficient funds error message',
            })
          )
          return
        }
        if (error?.code === SWIPER_ERROR_CODES.LSPAY_CARD_INCORRECT_ZIP) {
          // The inline error handled by `onCardElementChange` is not working anymore in this case
          // so we should explicitly call `setCardErrorMessage` to set the inline error message
          setCardErrorMessage(
            t('Your postal code is incomplete.', {
              _context: 'Lspay CNP inline incomplete zip error message',
            })
          )
          ToastNotifications.negative(
            t(COMPLETE_HIGHLIGHTED_FIELDS_ERROR_MESSAGE, {
              _context: 'Lspay CNP complete highlighted fields error message',
            })
          )
          return
        }
        if (error?.code === SWIPER_ERROR_CODES.LSPAY_CARD_INCORRECT_CVC) {
          setCardErrorMessage(
            t(`Your card's security code is incorrect.`, {
              _context:
                'Lspay CNP inline incorrect card security code error message',
            })
          )
          ToastNotifications.negative(
            t(COMPLETE_HIGHLIGHTED_FIELDS_ERROR_MESSAGE, {
              _context: 'Lspay CNP complete highlighted fields error message',
            })
          )
          return
        }
        if (error?.code === SWIPER_ERROR_CODES.LSPAY_CARD_EXPIRED) {
          setCardErrorMessage(
            t('Your card has expired.', {
              _context: 'Lspay CNP inline expired card error message',
            })
          )
          ToastNotifications.negative(
            t(COMPLETE_HIGHLIGHTED_FIELDS_ERROR_MESSAGE, {
              _context: 'Lspay CNP complete highlighted fields error message',
            })
          )
          return
        }

        // set a generic error message if the error is not a known reason
        setBannerErrorMessage(
          t(DEFAULT_PAYMENT_ERROR_MESSAGE, {
            _context: 'Lspay CNP failed to confirm payment error message',
          })
        )
      })
  }

  const onCardElementChange = (event: StripeCardElementChangeEvent) => {
    // set inline errors
    if (event.error) {
      const inlineErrorMessage = STRIPE_CNP_VALIDATION_ERRORS.includes(
        event.error.code as STRIPE_ERROR_CODE
      )
        ? getValidationErrorMessage(event.error.code as STRIPE_ERROR_CODE)
        : t(DEFAULT_INLINE_VALIDATION_ERROR_MESSAGE, {
            _context: 'Lspay CNP inline validation error message',
          })
      setCardErrorMessage(inlineErrorMessage)
    } else {
      setCardErrorMessage(undefined)
    }

    setIsValid(isValidCardElementInput(event))
  }

  return (
    <>
      {bannerErrorMessage && (
        <Banner variant="negative">{bannerErrorMessage}</Banner>
      )}
      <ModalContent>
        <div className="vd-field vd-mt5">
          <FieldLabel
            label={t('Card Details', {
              _context: 'Card details label',
            })}
          >
            <div
              className={processing ? 'StripeElement--disabled-container' : ''}
            >
              <CardElement
                options={{
                  disabled: processing,
                  style: cardElementStyle,
                }}
                onChange={onCardElementChange}
              />
            </div>
            {cardErrorMessage && (
              <ErrorMessage>{cardErrorMessage}</ErrorMessage>
            )}
          </FieldLabel>
        </div>
        <div className="vd-field">
          <FieldLabel
            label={t('Cardholder Name', {
              _context: 'Cardholder name label',
            })}
            supplementaryLabel="(Optional)"
          >
            <TextInput
              maxLength={5000}
              disabled={processing}
              placeholder="Enter name"
              onChange={event => setCardHolderName(event.target.value)}
            />
          </FieldLabel>
        </div>
      </ModalContent>
      <ModalActions>
        <div className="vd-btn-group">
          <Button
            data-track="cancel-payment-lspay-modal"
            variant="supplementary"
            onClick={close}
            disabled={processing}
          >
            {t('Cancel Payment', {
              _context: 'Cancel payment button',
            })}
          </Button>
          <Button
            onClick={hasMoto ? confirmWithSwiper : confirmWithStripe}
            loading={processing}
            disabled={!isValid}
            data-track="process-payment-lspay-modal"
          >
            {t('Process Payment', {
              _context: 'Process payment button',
            })}
          </Button>
        </div>
      </ModalActions>
    </>
  )
}
